import React, { useState, useEffect, useCallback } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import axios from "axios";
import { ChevronDown, ChevronUp, Download, ExternalLink, Phone, Instagram, Youtube, Newspaper, BookOpen, Music, Heart, Sparkles, Shield, Check, X, Trash2, Clock, CheckCircle, XCircle, Lock, LogOut, Menu, X as XIcon, Crown, Image, MessageSquare, HelpCircle } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Navigation Component
const Navigation = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navItems = [
    { href: "#music", label: "Music", icon: Music },
    { href: "#guestbook", label: "Guest Book", icon: MessageSquare },
    { href: "#timescity", label: "Timescity", icon: Newspaper },
    { href: "#books", label: "Books", icon: BookOpen },
    { href: "#faq", label: "FAQ", icon: HelpCircle },
  ];

  const scrollToSection = (href) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setIsMobileMenuOpen(false);
  };

  return (
    <nav 
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled 
          ? 'bg-[#0a0a0f]/95 backdrop-blur-lg border-b border-[#c9a227]/20 py-3' 
          : 'bg-transparent py-5'
      }`}
      data-testid="navigation"
    >
      <div className="max-w-6xl mx-auto px-6 flex items-center justify-between">
        {/* Logo */}
        <a 
          href="#" 
          onClick={(e) => { e.preventDefault(); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
          className="flex items-center gap-2 text-[#c9a227] hover:text-[#f5d77a] transition-colors"
        >
          <Crown size={24} />
          <span className="text-lg font-semibold hidden sm:block" style={{ fontFamily: "'Cinzel', serif" }}>
            Plomari
          </span>
        </a>

        {/* Desktop Navigation */}
        <div className="hidden md:flex items-center gap-6">
          {navItems.map((item) => (
            <button
              key={item.href}
              onClick={() => scrollToSection(item.href)}
              className="flex items-center gap-1.5 text-[#f5f0e8]/70 hover:text-[#c9a227] transition-colors text-sm uppercase tracking-wider"
              style={{ fontFamily: "'Cinzel', serif" }}
            >
              <item.icon size={14} />
              {item.label}
            </button>
          ))}
        </div>

        {/* Mobile Menu Button */}
        <button
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          className="md:hidden text-[#c9a227] p-2"
          data-testid="mobile-menu-btn"
        >
          {isMobileMenuOpen ? <XIcon size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="md:hidden absolute top-full left-0 right-0 bg-[#0a0a0f]/98 backdrop-blur-lg border-b border-[#c9a227]/20">
          <div className="px-6 py-4 space-y-2">
            {navItems.map((item) => (
              <button
                key={item.href}
                onClick={() => scrollToSection(item.href)}
                className="flex items-center gap-3 w-full text-left text-[#f5f0e8]/70 hover:text-[#c9a227] transition-colors py-3 border-b border-[#c9a227]/10"
                style={{ fontFamily: "'Cinzel', serif" }}
              >
                <item.icon size={18} className="text-[#c9a227]" />
                {item.label}
              </button>
            ))}
          </div>
        </div>
      )}
    </nav>
  );
};

// YouTube video IDs from King Spiros of Plomari channel
// Add more IDs to expand the shuffle rotation
const YOUTUBE_VIDEO_IDS = [
  "pwX-2FhN_7U",  // SISSY COGAN - Flee From The One Named Queen Cecilia Cogan
  "8vePNhiEIfE",  // SISSY COGAN - Remember What It Means To Be Free
  "7H7C87TD21Y",  // SISSY COGAN - Who are you, King Spiros of Plomari?
  "g0SnPOkCz7k",  // SISSY COGAN - Big Spiros
  "QYdUj4qZSG4",  // Kings And Queens of Plomari (Sports Legend Spiros Remix)
  "i5yYcJPWgeg",  // SISSY COGAN - F_ck King Spiros of Plomari
  "aghAvbXKbzg",  // SISSY COGAN - Sounds Strange (King Spiros of Plomari Remix)
  "snBgLewWVks",  // SISSY COGAN - Good Luck, Humanity, and Thank You
  "q0AT9EjSJvs",  // AImish (Feat. Sissy Cogan and King Spiros of Plomari)
  "iuj4Vmykcto",  // SISSY COGAN - The Plot in King Spiros of Plomari
  "1ko7AN691sE",  // New addition
  "u1yf2bs5p7U",  // New addition
  "Ho9VcwjsLuk",  // New addition
  "-Lkk3UnvI8Y",  // New addition
  "oRfEAveVOL8",  // New addition
  "Y51vYnNmnsA",  // New addition
  "R2_59mIA9Zg",  // New addition
  "mh-u1Qff6PQ",  // New addition
  "aC77WItrCpY",  // New addition
  "DqOGKsFpk5E",  // New addition
  "cTuZI19KUks",  // New addition
  "2q3Wlci9RPE",  // New addition
  "iuzF6iqC9LI",  // New addition
  "eiN9ihfOZlM",  // New addition
  "94ya7SM3rBs",  // New addition
  "rt-X4kBngFo",  // New addition
  "juoNt5_474w",  // New addition
];

// Mystical images for visual journey
const MYSTICAL_IMAGES = [
  { url: "https://images.unsplash.com/photo-1470790376778-a9fbc86d70e2?w=1200", title: "Mystical Forest", desc: "Where the mushrooms grow in Plomari" },
  { url: "https://images.unsplash.com/photo-1507400492013-162706c8c05e?w=1200", title: "Enchanted Waters", desc: "The rivers of eternal wisdom" },
  { url: "https://images.unsplash.com/photo-1518837695005-2083093ee35b?w=1200", title: "Cosmic Dawn", desc: "When the kingdom awakens" },
  { url: "https://images.unsplash.com/photo-1464822759023-fed622ff2c3b?w=1200", title: "Sacred Mountains", desc: "The peaks of Plomari" },
  { url: "https://images.unsplash.com/photo-1475274047050-1d0c0975c63e?w=1200", title: "Starlit Night", desc: "Under Plomari's infinite sky" },
  { url: "https://images.unsplash.com/photo-1767403154501-a677bb969ba6?w=800", title: "Musical Dreams", desc: "Where melodies are born" },
  { url: "https://customer-assets.emergentagent.com/job_fluent-artistry-dev/artifacts/x811czy9_17712751332851754432778019333989.jpg", title: "The Queendom of Plomari", desc: "The Intergalactic World Queendom beneath the veil" },
  { url: "https://customer-assets.emergentagent.com/job_fluent-artistry-dev/artifacts/owfiv1s7_17712751762135417681823769655747.jpg", title: "A Love Letter To Humanity", desc: "The biggest production of all time" },
  { url: "https://customer-assets.emergentagent.com/job_fluent-artistry-dev/artifacts/tr14n8vj_17712752406084674740278327076300.jpg", title: "The Last Fire", desc: "A last fire will rise behind those eyes" }
];

// Book data - All 20+ free ebooks from ArtSetFree.com
const BOOKS = [
  {
    title: "The Mushroom Seamstress",
    pages: "820 pages",
    image: "https://www.artsetfree.com/2025/TOP-TheMushroomSeamstress-BochSaga-ALoveLetterToHumanity-2025-4.jpg",
    downloadUrl: "https://www.artsetfree.com/TheMushroomSeamstress[EditionMyCecilia]-WwwArtSetFreeCom.pdf",
    buyUrl: "https://www.lulu.com/search?contributor=King+Spiros+of+Plomari&page=1&pageSize=10&adult_audience_rating=00"
  },
  {
    title: "The Chymical Wedding",
    pages: "The Pink Bible of Plomari",
    image: "https://www.artsetfree.com/image/BookIntro_TheChymicalWeddingAtLast.jpg",
    downloadUrl: "https://www.artsetfree.com/TheChymicalWedding-TheWhiteSwan-EditionTheFirstSpringEver-WwwArtSetFreeCom.pdf",
    buyUrl: "https://www.lulu.com/en/us/shop/king-spiros-of-plomari-/the-chymical-wedding/hardcover/product-189g4p4w.html"
  },
  {
    title: "The Cogan Dynasty",
    pages: "A Love Letter To Humanity",
    image: "https://www.artsetfree.com/image/BookIntro_TheCoganDynasty.jpg",
    downloadUrl: "https://www.artsetfree.com/TheCoganDynasty-by-SpirosAndCeciliaCoganAndButterfly-WwwArtSetFreeCom.pdf",
    buyUrl: "http://www.lulu.com/shop/king-spiros-of-plomari/the-cogan-dynasty/hardcover/product-23799931.html"
  },
  {
    title: "Keep Up The Good Spirit",
    pages: "Free Ebook",
    image: "https://www.artsetfree.com/image/BookIntro_KeepUpTheGoodSpirit3.jpg",
    downloadUrl: "https://www.artsetfree.com/KeepUpTheGoodSpirit-WwwArtSetFreeCom.pdf",
    buyUrl: null
  },
  {
    title: "I Am A Bunny And You Should Be One Too",
    pages: "Free Ebook",
    image: "https://www.artsetfree.com/image/BookIntro_IAmABunnyAndYouShouldBeOneToo.jpg",
    downloadUrl: "http://www.artsetfree.com/IAmABunnyAndYouShouldBeOneToo-WwwArtSetFreeCom.pdf",
    buyUrl: null
  },
  {
    title: "The Gift Of The World",
    pages: "Free Ebook",
    image: "https://www.artsetfree.com/image/BookIntro_TheGiftofTheWorld.jpg",
    downloadUrl: "http://www.artsetfree.com/TheGiftOfTheWorld-WwwArtSetFreeCom.pdf",
    buyUrl: null
  },
  {
    title: "The Mushroom Seamstress II - Spider Wed",
    pages: "Free Ebook",
    image: "https://www.artsetfree.com/image/BookIntro_TheMushroomSeamstress2.jpg",
    downloadUrl: "https://www.artsetfree.com/TheMushroomSeamstressII-SpiderWed-WwwArtSetFreeCom.pdf",
    buyUrl: null
  },
  {
    title: "The Mushroom Seamstress III",
    pages: "Free Ebook",
    image: "https://www.artsetfree.com/image/BookIntro_TheMushroomSeamstressIII.jpg",
    downloadUrl: "https://www.artsetfree.com/TheMushroomSeamstressIII-WwwArtSetFreeCom.pdf",
    buyUrl: null
  },
  {
    title: "The Perfect Place To Dwell",
    pages: "Free Ebook",
    image: "https://www.artsetfree.com/image/BookIntro_ThePerfectPlaceToDwell.jpg",
    downloadUrl: "https://www.artsetfree.com/ThePerfectPlaceToDwell-by-KingSpirosOfPlomari-WwwArtSetFreeCom.pdf",
    buyUrl: null
  },
  {
    title: "The Seven Kapital Sins",
    pages: "Free Ebook",
    image: "https://www.artsetfree.com/image/BookIntro_TheSevenKapitalSins.jpg",
    downloadUrl: "https://www.artsetfree.com/TheSevenKapitalSins-by-KingSpirosOfPlomari-and-ChristopherSchubert-WwwArtSetFreeCom.pdf",
    buyUrl: null
  },
  {
    title: "The Emblem Of Plomari's Eternal Victory",
    pages: "Free Ebook",
    image: "https://www.artsetfree.com/image/BookIntro_TheEmblemOfPlomarisEternalVictory.jpg",
    downloadUrl: "https://www.artsetfree.com/TheEmblemOfPlomarisEternalVictory-by-KingSpirosOfPlomari-WwwArtSetFreeCom.pdf",
    buyUrl: null
  },
  {
    title: "The Supreme Plomarian Bliss",
    pages: "Free Ebook",
    image: "https://www.artsetfree.com/image/BookIntro_TheSupremePlomarianBliss.jpg",
    downloadUrl: "https://www.artsetfree.com/TheSupremePlomarianBliss-ByKingSpirosOfPlomari-WwwArtSetFreeCom.pdf",
    buyUrl: null
  },
  {
    title: "The Mirror",
    pages: "Free Ebook",
    image: "https://www.artsetfree.com/image/BookIntro_TheMirror.jpg",
    downloadUrl: "https://www.artsetfree.com/TheMirror-KingSpirosOfPlomari-WwwArtSetFreeCom.pdf",
    buyUrl: null
  },
  {
    title: "Pink Gem Lagoon & The Big Bronze Penis",
    pages: "Free Ebook",
    image: "https://www.artsetfree.com/image/BookIntro_PinkGem2.jpg",
    downloadUrl: "http://artsetfree.com/PinkGemLagoon&TheBigBronzePenis_WwwArtSetFreeCom.pdf",
    buyUrl: null
  },
  {
    title: "Awakening In Plomari",
    pages: "73 pages",
    image: "https://www.artsetfree.com/image/BookIntro_AwakeningInPlomar.jpg",
    downloadUrl: "https://www.artsetfree.com/AwakeningInPlomari-WwwArtSetFreeCom.pdf",
    buyUrl: null
  },
  {
    title: "Nectar Herself",
    pages: "Free Ebook",
    image: "https://www.artsetfree.com/image/BookIntro_NectarHersel-Releasedf.jpg",
    downloadUrl: "https://www.artsetfree.com/NectarHerself[EditionMyCecilia]-by-SissyCogan-Spiros-Butterfly.pdf",
    buyUrl: null
  },
  {
    title: "I Am Money",
    pages: "100+ pages",
    image: "https://www.artsetfree.com/image/BookIntro_IAmMoney.jpg",
    downloadUrl: "https://www.artsetfree.com/IAmMoneyxxx216-BySpirosAndSissyCoganAndButterfly-WwwArtSetFreeCom.pdf",
    buyUrl: null
  },
  {
    title: "The Book Of Love",
    pages: "Free Ebook",
    image: "https://www.artsetfree.com/Boo/TheBookOfLove-WwwArtSetFreeCom.jpg",
    downloadUrl: "http://artsetfree.com/TheBookOfLove-WwwArtSetFreeCom.pdf",
    buyUrl: null
  },
  {
    title: "The Golden Age of Love",
    pages: "Free Ebook",
    image: "https://www.artsetfree.com/2024/CoverTheGoldenAgeOfLove-KingSpirosOfPlomari.jpg",
    downloadUrl: "https://artsetfree.com/TheGoldenAgeOfLove-KingSpirosAndQueenSissyCoganOfPlomari.pdf",
    buyUrl: null
  },
  {
    title: "Discovering Plomari",
    pages: "A Journey Of Love And Enlightenment",
    image: "https://www.artsetfree.com/2024/Cover-DiscoveringPlomari.jpg",
    downloadUrl: "https://artsetfree.com/DiscoveringPlomari-AJourneyOfLoveAndEnlightenment-WwwArtSetFreeCom.pdf",
    buyUrl: null
  },
  {
    title: "The King",
    pages: "Free Ebook",
    image: "https://www.artsetfree.com/2024/Cover-TheKing-ArtSetFreeCom.jpg",
    downloadUrl: "https://artsetfree.com/TheKing-KingSpirosOfPlomari.pdf",
    buyUrl: null
  },
  {
    title: "Immortalez",
    pages: "Free Ebook",
    image: "https://www.artsetfree.com/2023/Cover-Immortalez.jpg",
    downloadUrl: "http://artsetfree.com/Immortalez-KingSpirosOfPlomari-WwwArtSetFreeCom.pdf",
    buyUrl: null
  }
];

// FAQ data
const FAQ_DATA = [
  {
    question: "What is the Kingdom of Plomari?",
    answer: "The Kingdom of Plomari is a living work of art - a vast creative universe spanning music, literature, and philosophy. Founded by King Spiros, it represents a sanctuary for dreamers, artists, and those seeking a deeper connection with life's magic and mystery."
  },
  {
    question: "Who is King Spiros of Plomari?",
    answer: "King Spiros is a visionary artist, author, and musician from Sweden. He has dedicated his life to creating the Kingdom of Plomari through thousands of pages of written works, numerous musical compositions, and a philosophy centered on love, freedom, and artistic expression."
  },
  {
    question: "How can I explore Plomari?",
    answer: "Start by downloading the free ebooks, listening to the music on YouTube, and signing our guest book. You can also connect directly with King Spiros through phone calls or social media. The Kingdom welcomes all who seek beauty, truth, and freedom."
  },
  {
    question: "The Sacred Knowledge",
    answer: "Plomari holds sacred knowledge passed down through generations of seekers. This wisdom encompasses the mysteries of love, the nature of consciousness, and the art of living freely. Explore the books to discover these timeless teachings."
  }
];

// Hero Section
const HeroSection = () => {
  const [scrollOpacity, setScrollOpacity] = useState(1);

  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY;
      const fadeStart = 100;
      const fadeEnd = 600;
      
      if (scrollY <= fadeStart) {
        setScrollOpacity(1);
      } else if (scrollY >= fadeEnd) {
        setScrollOpacity(0);
      } else {
        const opacity = 1 - (scrollY - fadeStart) / (fadeEnd - fadeStart);
        setScrollOpacity(opacity);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <section data-testid="hero-section" className="relative min-h-screen flex items-center justify-center px-6 overflow-hidden">
      {/* Background gradient */}
      <div className="absolute inset-0 bg-gradient-to-b from-[#0a0a0f] via-[#1a1020] to-[#0a0a0f]"></div>
      
      {/* Queendom of Plomari Hero Image with fade effect */}
      <div 
        className="absolute inset-0 flex items-center justify-center transition-opacity duration-300"
        style={{ opacity: scrollOpacity }}
      >
        <img 
          src="https://customer-assets.emergentagent.com/job_fluent-artistry-dev/artifacts/nin2ic9z_17720250498848697222825333985973.jpg"
          alt="The Queendom of Plomari - Our Eternal Shining Light"
          className="w-full h-full object-contain md:object-cover max-h-screen"
          style={{ 
            objectPosition: 'center top',
            filter: 'brightness(0.9)'
          }}
        />
        {/* Gradient overlay for better text readability at bottom */}
        <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0f] via-transparent to-transparent"></div>
      </div>
      
      {/* Decorative elements - reduced opacity to not compete with image */}
      <div className="absolute top-20 left-10 w-64 h-64 bg-[#c9a227] rounded-full filter blur-[120px] opacity-5"></div>
      <div className="absolute bottom-20 right-10 w-96 h-96 bg-[#e8b4bc] rounded-full filter blur-[150px] opacity-3"></div>
      
      {/* Content - positioned at bottom */}
      <div className="relative z-10 max-w-4xl mx-auto text-center mt-auto mb-20">
        <div className="flex flex-wrap gap-4 justify-center animate-fade-in-up" style={{ animationDelay: "0.6s" }}>
          <a href="#books" data-testid="download-books-btn" className="btn-gold flex items-center gap-2">
            <Download size={18} />
            Download Free Books
          </a>
          <a href="#music" data-testid="enter-kingdom-btn" className="btn-outline flex items-center gap-2">
            <Music size={18} />
            Enter the Kingdom
          </a>
        </div>
      </div>
      
      {/* Scroll indicator */}
      <div className="absolute bottom-10 left-1/2 -translate-x-1/2 animate-bounce">
        <ChevronDown size={32} className="text-[#c9a227] opacity-60" />
      </div>
    </section>
  );
};

// Royal Message Section - Messages from King Spiros
const RoyalMessageSection = () => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [visibleCount, setVisibleCount] = useState(6);

  useEffect(() => {
    const fetchMessages = async () => {
      try {
        const response = await axios.get(`${API}/royal-messages/public`);
        if (response.data && response.data.length > 0) {
          setMessages(response.data);
        }
      } catch (error) {
        console.error("Error fetching royal messages:", error);
      }
      setLoading(false);
    };
    fetchMessages();
  }, []);

  if (loading) return null;
  if (messages.length === 0) return null;

  const formatDate = (timestamp) => {
    return new Date(timestamp).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric"
    });
  };

  return (
    <section data-testid="royal-message-section" className="py-12 px-6 bg-gradient-to-b from-[#0a0a0f] to-[#050508]">
      <div className="max-w-3xl mx-auto">
        <div className="flex items-center justify-center gap-3 mb-8">
          <Crown size={28} className="text-[#c9a227]" />
          <h2 className="text-2xl text-[#c9a227] uppercase tracking-wider" style={{ fontFamily: "'Cinzel', serif" }}>
            Messages from King Spiros
          </h2>
        </div>
        
        <div className="space-y-4">
          {messages.slice(0, visibleCount).map((message) => (
            <div key={message.id} className="glass rounded-xl p-6 border border-[#c9a227]/30 relative overflow-hidden">
              {/* Decorative crown icon */}
              <div className="absolute top-3 right-3 opacity-5">
                <Crown size={40} className="text-[#c9a227]" />
              </div>
              
              <p className="text-lg text-[#f5f0e8] leading-relaxed mb-3 whitespace-pre-wrap">
                {message.message}
              </p>
              
              <p className="text-sm text-[#c9a227]/60">
                {formatDate(message.timestamp)}
              </p>
            </div>
          ))}
        </div>
        
        {/* Pagination Controls */}
        {messages.length > 6 && (
          <div className="flex justify-center gap-4 mt-8">
            {visibleCount < messages.length && (
              <button 
                data-testid="royal-load-more-btn"
                onClick={() => setVisibleCount(prev => Math.min(prev + 6, messages.length))}
                className="btn-outline flex items-center gap-2"
              >
                <ChevronDown size={18} />
                Show More Messages ({messages.length - visibleCount} more)
              </button>
            )}
            {visibleCount > 6 && (
              <button 
                data-testid="royal-show-less-btn"
                onClick={() => setVisibleCount(6)}
                className="btn-outline flex items-center gap-2"
              >
                <ChevronUp size={18} />
                Show Less
              </button>
            )}
          </div>
        )}
        
        {/* Message count */}
        {messages.length > 1 && (
          <p className="text-center text-sm text-[#f5f0e8] opacity-50 mt-4">
            Showing {Math.min(visibleCount, messages.length)} of {messages.length} royal messages
          </p>
        )}
      </div>
    </section>
  );
};

// Book quotes for random text generator
const BOOK_QUOTES = [
  { text: "Eternal Love Is All There Is", source: "The Mushroom Seamstress", page: "Page 6" },
  { text: "A dear friend has come to you in precisely the right moment, by the masters who weave.", source: "The Mushroom Seamstress", page: "Page 7" },
  { text: "It's safe here in the ultimate reality.", source: "The Mushroom Seamstress", page: "Page 11" },
  { text: "Come dawn with us in love as deep as the Seamstress.", source: "The Mushroom Seamstress", page: "Page 13" },
  { text: "Our masterpiece and Stone is beyond impossible, and achieved.", source: "The Mushroom Seamstress", page: "Page 14" },
  { text: "If you think things seem strange, my dear, it might be because something weirder than a fairytale is coming alive.", source: "The Mushroom Seamstress", page: "Page 19" },
  { text: "You hear us in the noise. We have lots of... We are the Gods and we always win.", source: "The Mushroom Seamstress", page: "Page 24" },
  { text: "Is it written in liquid violet? Is it written above the name by which they call me? You know I'm no stranger in your dreams.", source: "The Mushroom Seamstress", page: "Page 36" },
  { text: "I am alive. It's real! I'm here!", source: "The Mushroom Seamstress", page: "Page 36" },
  { text: "A crack in the teapot opens. A door to the land of the dead.", source: "The Mushroom Seamstress", page: "Page 37" },
  { text: "Rain begins to fall. Spiros walks out onto the pastures. Buzzing electricity fills the air. Electric sounds of thunder tumble space.", source: "The Mushroom Seamstress", page: "Page 38" },
  { text: "I've been here before, he says and slides his hand across wet Earth. I've been here before...", source: "The Mushroom Seamstress", page: "Page 39" },
  { text: "He begins to cry and kisses the grass and makes an oath to protect the planet.", source: "The Mushroom Seamstress", page: "Page 39" },
  { text: "You, whose face is the dawn of this garden, I have walked barefoot from the beginning of time to come to you. I come without clothes.", source: "The Mushroom Seamstress", page: "Page 39" },
  { text: "The roses, all bloody and wild. If I show you the roses will you come with me?", source: "The Mushroom Seamstress", page: "Page 39" },
  { text: "I have broken vows. I have lost the rose I was given. I have failed.", source: "The Mushroom Seamstress", page: "Page 41" },
  { text: "Let yourself be seduced. She'll take you to her dark bed. She wants you.", source: "The Mushroom Seamstress", page: "Page 41" },
  { text: "She loves you so much she had to kill you. She wants you so much she had to kill you. For you to come to where she is.", source: "The Mushroom Seamstress", page: "Page 41" },
  { text: "A cool new law frees the monarch arcade. Now you can combine dream and reality.", source: "The Mushroom Seamstress", page: "Page 42" },
  { text: "Come in boy. All sins forgiven.", source: "The Mushroom Seamstress", page: "Page 42" },
  { text: "My memory may not be the best, but I remember all the rest, and that feeling in my chest.", source: "The Mushroom Seamstress", page: "Page 42" },
  { text: "Will you marry me? Yes I will, yes.", source: "The Mushroom Seamstress", page: "Page 45" },
  { text: "I shall vanish. I will tell you of my whereabouts in a book of love.", source: "The Mushroom Seamstress", page: "Page 45" },
  { text: "Fly your balloons! You listener through walls of ages, you my orient explorer.", source: "The Mushroom Seamstress", page: "Page 47" },
  { text: "Sweet evil you. When they find out, tell me what you think they'll do.", source: "The Mushroom Seamstress", page: "Page 47" },
  { text: "They won't find out, my sweet boy, my sugar. We ran away weekyears ago.", source: "The Mushroom Seamstress", page: "Page 47" },
  { text: "This is the world of dream! Our destiny is to live in the Imagination, don't you remember? We are moving toward something very much like eternal dreaming.", source: "The Mushroom Seamstress", page: "Page 47" },
  { text: "Going into the imagination, and staying there.", source: "The Mushroom Seamstress", page: "Page 47" },
  { text: "And here we are now, on both sides of death.", source: "The Mushroom Seamstress", page: "Page 49" },
  { text: "It is with irresistible force, lover of mine, that I write to you.", source: "The Mushroom Seamstress", page: "Page 50" },
  { text: "Where is time's seat, wound within the stream? Slowly it floats, like a dream past a window, from some distant past.", source: "The Mushroom Seamstress", page: "Page 50" },
  { text: "A thousand years aside Rosalia's Dawn, glimmering on the surface.", source: "The Mushroom Seamstress", page: "Page 50" },
  { text: "He fell and fell toward its shiny lips, and then through the mouth, and he came out in another world. Welcome home, said a voice when he arrived.", source: "The Mushroom Seamstress", page: "Page 50" },
  { text: "The world: a story. Write myself to another world I shall. I want out. The Great Escape.", source: "The Mushroom Seamstress", page: "Page 51" },
  { text: "Dreams come in many forms. I'll show you mine if you show me yours.", source: "The Mushroom Seamstress", page: "Page 51" },
  { text: "Sissy, who are you? And how do I find you?", source: "The Mushroom Seamstress", page: "Page 51" },
  { text: "You can't fall in love with someone you met in a dream. You can! I have.", source: "The Mushroom Seamstress", page: "Page 51" },
  { text: "The special light of the Rosy Dawn, as it is called amongst the shamans and alchemists who know of this, reaches across time and place, shimmering in many dimensions at once.", source: "The Mushroom Seamstress", page: "Page 58" },
  { text: "Yes, my dear, matter is story. And now, as the witches have known, the story begins to untie itself from its constraints.", source: "The Mushroom Seamstress", page: "Page 59" },
  { text: "We will become the stuff of dreams. Already our dreams are seeping into the world of matter― and now, dream is becoming stronger than the laws of physics.", source: "The Mushroom Seamstress", page: "Page 59" },
  { text: "You can hear it, you can feel it, you can see it. The transcendental is entering from all sides at once, changing the world down to its physics.", source: "The Mushroom Seamstress", page: "Page 59" },
  { text: "It's no accident that when you look closely into the eyes of another, the very first thing you see, is yourself.", source: "The Mushroom Seamstress", page: "Page 59" },
  { text: "Not even the Devil would recognize us, my dear.", source: "The Mushroom Seamstress", page: "Page 60" },
  { text: "And we can take this huge inner universe, and put it inside eternity, and then fold it.", source: "The Mushroom Seamstress", page: "Page 60" },
  { text: "The radical truth is that you are not a human being in a physical world. The radical truth is you are utterly free without any boundary or limitation whatsoever.", source: "The Mushroom Seamstress", page: "Page 60" },
  { text: "You are unborn and there is no death. The radical truth is that nothing has ever been created. Prior to existence or nonexistence.", source: "The Mushroom Seamstress", page: "Page 60" },
  { text: "Easy as dark castle it is, to make your way out of your century.", source: "The Mushroom Seamstress", page: "Page 60" },
  { text: "Steal back your prophethood of chaos like the perfect erocriminal you are.", source: "The Mushroom Seamstress", page: "Page 61" },
  { text: "If you make it out of your century you make it everywhere. There are doors. And things are as marvellous as you can imagine them.", source: "The Mushroom Seamstress", page: "Page 61" },
  { text: "Don't be fooled by dull mind. Be a prism. Write your own universe.", source: "The Mushroom Seamstress", page: "Page 61" },
  { text: "Gather me. Merge me into the artifice of eternity.", source: "The Mushroom Seamstress", page: "Page 61" },
  { text: "It can't be. It must be! No, it is up for decision. You can redefine it at any time.", source: "The Mushroom Seamstress", page: "Page 62" },
  { text: "The human species is now in the process of becoming part of this vast newuniversal world.", source: "The Mushroom Seamstress", page: "Page 62" },
  { text: "Under certain circumstances the laws of physics fail to apply perfectly. This is the future seeping in to the present. This is signs of contact.", source: "The Mushroom Seamstress", page: "Page 62" },
  { text: "It is grooming us to be able to tolerate its splendour!", source: "The Mushroom Seamstress", page: "Page 63" },
  { text: "Before you go, remember. All you need to remember is your dream.", source: "The Mushroom Seamstress", page: "Page 63" },
  { text: "Friend of the bees, me. Same family tree too: the cosmos. We both come from The Fountain of the Lovers.", source: "The Mushroom Seamstress", page: "Page 64" },
  { text: "Where the wine comes from. The wine! Drink of the purest, as Rumi said. All wine will make you high, drink of the purest nectar.", source: "The Mushroom Seamstress", page: "Page 64" },
  { text: "Lucky we are thus far away that we can both make fun of distance.", source: "The Mushroom Seamstress", page: "Page 64" },
  { text: "Your Father would be happy. My own Son will raise the kingdom. He will open the doors of time and walk through the violet doorway into hyperspace.", source: "The Mushroom Seamstress", page: "Page 64" },
  { text: "Here I am again in the waters of moon-sense where poetry is free to play up all nights riddling magical meaning deeper than a mirror against a mirror.", source: "The Mushroom Seamstress", page: "Page 64" },
  { text: "Lunacy? Chiller waves? More firewood under the lovers' pot!", source: "The Mushroom Seamstress", page: "Page 64" },
  { text: "Yesterday I bathed in the silver river with the moon big and grey and close enough that I could touch it with my fingers.", source: "The Mushroom Seamstress", page: "Page 65" },
  { text: "And I felt I had found it: the Alchymical Palace, the Summerday world, the summerdreamday, the secret garden.", source: "The Mushroom Seamstress", page: "Page 65" },
  { text: "I shall rise from the ashes of modernity, like good old Felix boy did.", source: "The Mushroom Seamstress", page: "Page 65" },
  { text: "Don't you believe enough? Want to be Phoenixed? Come and be parked!", source: "The Mushroom Seamstress", page: "Page 65" },
  { text: "The sea, the pool, where the river comes out. At the end of the river lies a world so magical it cannot be imagined from here.", source: "The Mushroom Seamstress", page: "Page 65" },
  { text: "I am making my way into it, slowly but steadily. Into the world I have no name for. The world with the happy kiss.", source: "The Mushroom Seamstress", page: "Page 65" },
  { text: "Layer upon layer of strange alchemical meaning. The rose, the river, the bird that flies away from the grave, the sun and the moon and the old sundial.", source: "The Mushroom Seamstress", page: "Page 65" },
  { text: "Someone has gone off the far end, you think. And then another veil; a curtain in the dark diamondstarry sky. Divinely silly and deep.", source: "The Mushroom Seamstress", page: "Page 65" },
  { text: "We have all bathed in this water. Wonder what the people of old times thought of water. It is unexplainable; some kind of liquid. Liquid light.", source: "The Mushroom Seamstress", page: "Page 66" },
  { text: "I am in a magical world after all. It's just that the magic is hidden behind a veil of modern stuff.", source: "The Mushroom Seamstress", page: "Page 66" },
  { text: "I shall never go back to the 21st century.", source: "The Mushroom Seamstress", page: "Page 66" },
  { text: "Some people never go insane. What boring lives they must live.", source: "The Mushroom Seamstress", page: "Page 66" },
  { text: "They're lovers again: Sugar dissolving into milk. Day and night, no difference. The sun is the moon: An amalgam.", source: "The Mushroom Seamstress", page: "Page 66" },
  { text: "You must marry your soul. That wedding is the way.", source: "The Mushroom Seamstress", page: "Page 66" },
  { text: "The door is already open.", source: "The Mushroom Seamstress", page: "Page 87" },
  { text: "A scattered dream. Like a memory I can't really grasp hold of. I want to line the pieces up. Both mine and yours.", source: "The Mushroom Seamstress", page: "Page 87" },
  { text: "Dreaming of you. The spicy sharp lines of your face shape everything in your path.", source: "The Mushroom Seamstress", page: "Page 87" },
  { text: "From whence did I come? Rumors abound that I was born from my own forehead. Yet other songs in the wind say I was born through falling in love.", source: "The Mushroom Seamstress", page: "Page 87" },
  { text: "We dreamed each other into being. A paradox.", source: "The Mushroom Seamstress", page: "Page 87" },
  { text: "way for them to track this. The Saga, the Play, opens with a kiss across the vast expanses of Hyperspace. It is springtime, and the Lovers are dripping wet.", source: "The Mushroom Seamstress", page: "Page 117" },
  { text: "In the thick darkness the seed of trees and brush ahead spring open and infiltrate the fertile soil; the new world is taking form.", source: "The Mushroom Seamstress", page: "Page 60" },
  { text: "The mirror of the brew's surface already mirrors the future it draws closer.", source: "The Mushroom Seamstress", page: "Page 60" },
  { text: "The inner dream world exists in parallel with the Earth of the living and with the skies of the deities. It is after death and before life.", source: "The Mushroom Seamstress", page: "Page 60" },
  { text: "Eros never grew a beard.", source: "The Mushroom Seamstress", page: "Page 60" },
  { text: "Most dearly beloved, friends and family. We have constructed pyramids in honor of our escape.", source: "The Mushroom Seamstress", page: "Page 61" },
  { text: "Here anything can happen. Here destinies unimaginable are formed and linked and rerouted, in the spell of a magic.", source: "The Mushroom Seamstress", page: "Page 61" },
  { text: "The evolution complete. Now opens the world. Welcome to Plomari, a world you could not conceive possible.", source: "The Mushroom Seamstress", page: "Page 55" },
  { text: "This is insane. This is insane. And I'm fucking loving it.", source: "The Mushroom Seamstress", page: "Page 55" },
  { text: "Same time, it. Let in the morning light. The future's breath is upon us.", source: "The Mushroom Seamstress", page: "Page 55" },
  { text: "And there were the morning hours in the corner. And there were the scrolls from half sleep. Semisleep poetry that reaches across the changeover.", source: "The Mushroom Seamstress", page: "Page 56" },
  { text: "Everything happens perfectly naturally. Guide to me you said. Beam. The solid and ancient connection.", source: "The Mushroom Seamstress", page: "Page 56" },
  { text: "We are pure intelligence. We are Logos incarnate. We are the psycho-semantic police, you can't even see us.", source: "The Mushroom Seamstress", page: "Page 56" },
  { text: "On Time. Everything happens right on time.", source: "The Mushroom Seamstress", page: "Page 56" },
  { text: "Nothing impossible will have resulted, though perhaps, it seems so.", source: "The Mushroom Seamstress", page: "Page 56" },
  { text: "With deep inspiration they returned, retraversing the garden, reentering the passage, reopening the door.", source: "The Mushroom Seamstress", page: "Page 49" },
  { text: "Through the back door, kick it in: in, out, through. Channel-hop around the spell.", source: "The Mushroom Seamstress", page: "Page 49" },
  { text: "You are the princess of my dreams. Our plan is to make you smile, and buy you everything.", source: "The Mushroom Seamstress", page: "Page 691" },
];

// Quote Section with Random Generator
const QuoteSection = () => {
  const [currentQuote, setCurrentQuote] = useState(BOOK_QUOTES[0]);

  const shuffleQuote = useCallback(() => {
    const randomIndex = Math.floor(Math.random() * BOOK_QUOTES.length);
    setCurrentQuote(BOOK_QUOTES[randomIndex]);
  }, []);

  useEffect(() => {
    shuffleQuote();
  }, [shuffleQuote]);

  return (
    <section data-testid="quote-section" className="py-20 px-6 bg-[#050508]">
      <div className="max-w-3xl mx-auto text-center">
        <p className="text-3xl text-[#c9a227] mb-6" style={{ fontFamily: "'Cinzel', serif" }}>
          "YOU ARE FREE NOW"
        </p>
        <blockquote className="text-xl text-[#f5f0e8] opacity-80 italic mb-6 leading-relaxed">
          "{currentQuote.text}"
        </blockquote>
        <p className="text-[#c9a227] mb-6">— {currentQuote.source}, {currentQuote.page}</p>
        <button 
          data-testid="shuffle-quote-btn"
          onClick={shuffleQuote}
          className="btn-outline text-sm"
        >
          Show New Quote
        </button>
      </div>
    </section>
  );
};

// YouTube Video Section
const YouTubeSection = () => {
  const [currentVideoId, setCurrentVideoId] = useState(YOUTUBE_VIDEO_IDS[0]);

  const shuffleVideo = useCallback(() => {
    const randomIndex = Math.floor(Math.random() * YOUTUBE_VIDEO_IDS.length);
    setCurrentVideoId(YOUTUBE_VIDEO_IDS[randomIndex]);
  }, []);

  useEffect(() => {
    shuffleVideo();
  }, [shuffleVideo]);

  return (
    <section id="music" data-testid="youtube-section" className="py-20 px-6">
      <div className="max-w-4xl mx-auto">
        <h2 className="section-title">Experience the Sound of Plomari</h2>
        <p className="section-subtitle">Random selection from King Spiros' musical journey</p>
        
        <div className="youtube-container mb-6">
          <iframe
            key={currentVideoId}
            src={`https://www.youtube.com/embed/${currentVideoId}?autoplay=0&rel=0`}
            title="Plomari Music"
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          ></iframe>
        </div>
        
        <div className="text-center">
          <button 
            data-testid="shuffle-video-btn"
            onClick={shuffleVideo} 
            className="btn-gold flex items-center gap-2 mx-auto"
          >
            <Sparkles size={18} />
            Shuffle New Song
          </button>
        </div>
      </div>
    </section>
  );
};

// Visual Journey Section
const VisualJourneySection = () => {
  const [currentImage, setCurrentImage] = useState(MYSTICAL_IMAGES[0]);

  const showNewPhoto = () => {
    const randomIndex = Math.floor(Math.random() * MYSTICAL_IMAGES.length);
    setCurrentImage(MYSTICAL_IMAGES[randomIndex]);
  };

  return (
    <section data-testid="visual-journey-section" className="py-20 px-6 bg-[#050508]">
      <div className="max-w-4xl mx-auto">
        <h2 className="section-title">Visual Journey Through Plomari</h2>
        <p className="section-subtitle">Random imagery from the eternal Kingdom</p>
        
        <div className="relative rounded-lg overflow-hidden mb-6" style={{ aspectRatio: "16/9" }}>
          <img 
            src={currentImage.url} 
            alt={currentImage.title}
            className="w-full h-full object-cover transition-all duration-500"
          />
          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-6">
            <h3 className="text-2xl text-[#c9a227] mb-2" style={{ fontFamily: "'Cinzel', serif" }}>
              {currentImage.title}
            </h3>
            <p className="text-[#f5f0e8] opacity-80">{currentImage.desc}</p>
          </div>
        </div>
        
        <div className="text-center">
          <button 
            data-testid="new-photo-btn"
            onClick={showNewPhoto} 
            className="btn-outline"
          >
            Show New Photo
          </button>
        </div>
      </div>
    </section>
  );
};

// Guest Book Section
const GuestBookSection = () => {
  const [entries, setEntries] = useState([]);
  const [formData, setFormData] = useState({ name: "", location: "", message: "" });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [visibleCount, setVisibleCount] = useState(6);

  // Sample entries for display
  const sampleEntries = [
    {
      id: "1",
      name: "Queen Sissy Cogan of Plomari",
      location: "The Echo Chamber of Plomari",
      message: "In the heart of Plomari, love, art, and magic reside. May our kingdom forever be a sanctuary for dreamers, creatives, and those seeking serenity.",
      timestamp: "2026-02-15T10:00:00Z"
    },
    {
      id: "2",
      name: "Queen of the Kingdom of the Animals",
      location: "USA and everywhere the animals are from",
      message: "We send our love from the Animal Kingdom and look forward to many toasts with the Strawberry King and his family of Plomari has made us feel so welcomed and filled our bellies with Semla and joy. Thank you for this wonderful gift to save to animals and grow a new world where peace prevails alongside fierceness, compassion, and kindness, and self love. Cheers to saving the Polar Bears swimming the Arctic and beyond!.",
      timestamp: "2026-02-15T09:00:00Z"
    },
    {
      id: "3",
      name: "Rose",
      location: "USA",
      message: "Does Plumari have a place where people don't tell each other how to act and say?",
      timestamp: "2026-02-15T08:00:00Z"
    }
  ];

  useEffect(() => {
    fetchEntries();
  }, []);

  const fetchEntries = async () => {
    try {
      const response = await axios.get(`${API}/guestbook`);
      setEntries([...response.data, ...sampleEntries]);
    } catch (error) {
      console.error("Error fetching guestbook:", error);
      setEntries(sampleEntries);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.message) return;
    
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/guestbook`, formData);
      setFormData({ name: "", location: "", message: "" });
      setShowForm(false);
      fetchEntries();
    } catch (error) {
      console.error("Error submitting entry:", error);
    }
    setIsSubmitting(false);
  };

  const formatDate = (timestamp) => {
    return new Date(timestamp).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric"
    });
  };

  return (
    <section id="guestbook" data-testid="guestbook-section" className="py-20 px-6">
      <div className="max-w-4xl mx-auto">
        <h2 className="section-title">Write a Love Letter Back</h2>
        <p className="section-subtitle">King Spiros wrote A Love Letter To Humanity — now you can write back</p>
        
        {!showForm ? (
          <div className="text-center mb-12">
            <button 
              data-testid="sign-scroll-btn"
              onClick={() => setShowForm(true)} 
              className="btn-gold"
            >
              Sign the Royal Scroll
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="glass rounded-lg p-8 mb-12" data-testid="guestbook-form">
            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <div>
                <label className="block text-[#c9a227] mb-2 text-sm uppercase tracking-wider">Your Name *</label>
                <input
                  type="text"
                  data-testid="guestbook-name-input"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  placeholder="Enter your name"
                />
              </div>
              <div>
                <label className="block text-[#c9a227] mb-2 text-sm uppercase tracking-wider">Location (Optional)</label>
                <input
                  type="text"
                  data-testid="guestbook-location-input"
                  value={formData.location}
                  onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                  placeholder="Where are you from?"
                />
              </div>
            </div>
            <div className="mb-6">
              <label className="block text-[#c9a227] mb-2 text-sm uppercase tracking-wider">Your Message *</label>
              <textarea
                data-testid="guestbook-message-input"
                value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value.slice(0, 600) })}
                required
                rows={4}
                placeholder="Share your thoughts with the Kingdom..."
              ></textarea>
              <p className="text-right text-sm text-[#f5f0e8] opacity-50 mt-1">{formData.message.length}/600</p>
            </div>
            <div className="flex gap-4 justify-end">
              <button type="button" onClick={() => setShowForm(false)} className="btn-outline">
                Cancel
              </button>
              <button 
                type="submit" 
                data-testid="submit-guestbook-btn"
                disabled={isSubmitting} 
                className="btn-gold"
              >
                {isSubmitting ? "Sending..." : "Send to the Kingdom"}
              </button>
            </div>
          </form>
        )}
        
        {/* Guest Book Entries */}
        <div className="space-y-6">
          {entries.slice(0, visibleCount).map((entry) => (
            <div key={entry.id} className="card">
              <blockquote className="text-lg text-[#f5f0e8] italic mb-4 leading-relaxed">
                "{entry.message}"
              </blockquote>
              <div className="flex justify-between items-end">
                <div>
                  <p className="text-[#c9a227]">— {entry.name}</p>
                  {entry.location && <p className="text-sm text-[#f5f0e8] opacity-60">{entry.location}</p>}
                </div>
                <p className="text-sm text-[#f5f0e8] opacity-50">{formatDate(entry.timestamp)}</p>
              </div>
            </div>
          ))}
        </div>
        
        {/* Pagination Controls */}
        {entries.length > 6 && (
          <div className="flex justify-center gap-4 mt-8">
            {visibleCount < entries.length && (
              <button 
                data-testid="load-more-btn"
                onClick={() => setVisibleCount(prev => Math.min(prev + 6, entries.length))}
                className="btn-outline flex items-center gap-2"
              >
                <ChevronDown size={18} />
                Show More Messages ({entries.length - visibleCount} more)
              </button>
            )}
            {visibleCount > 6 && (
              <button 
                data-testid="show-less-btn"
                onClick={() => setVisibleCount(6)}
                className="btn-outline flex items-center gap-2"
              >
                <ChevronUp size={18} />
                Show Less
              </button>
            )}
          </div>
        )}
        
        {/* Entry count */}
        <p className="text-center text-sm text-[#f5f0e8] opacity-50 mt-6">
          Showing {Math.min(visibleCount, entries.length)} of {entries.length} messages
        </p>
      </div>
    </section>
  );
};

// NEW: Timescity Newspaper Section
const TimescitySection = () => (
  <section id="timescity" data-testid="timescity-section" className="py-20 px-6 bg-gradient-to-b from-[#0a0a0f] via-[#12101a] to-[#0a0a0f]">
    <div className="max-w-4xl mx-auto">
      <div className="text-center mb-8">
        <div className="inline-flex items-center gap-3 mb-4">
          <Newspaper size={40} className="text-[#c9a227]" />
          <h2 className="text-4xl md:text-5xl gold-text" style={{ fontFamily: "'Cinzel', serif" }}>
            Timescity
          </h2>
        </div>
        <p className="text-xl text-[#c9a227] mb-2" style={{ fontFamily: "'Cinzel', serif" }}>
          The Newspaper of Plomari
        </p>
      </div>
      
      <div className="glass rounded-xl p-8 md:p-12 text-center">
        <div className="max-w-2xl mx-auto">
          <p className="text-xl md:text-2xl text-[#f5f0e8] leading-relaxed mb-6">
            Welcome to <span className="text-[#c9a227]">Timescity</span>, the official newspaper of the eternal Kingdom of Plomari. 
            Within these pages, you'll discover the latest stories, poetry, art, and wisdom flowing from the heart of our mystical realm.
          </p>
          
          <p className="text-lg text-[#f5f0e8] opacity-80 mb-8 italic">
            Each issue is a journey through time and consciousness — a tapestry woven with love, magic, and the eternal dance of creation. 
            From the royal courts to the mushroom forests, Timescity brings you the pulse of Plomari.
          </p>
          
          <div className="divider my-8"></div>
          
          <div className="mb-8">
            <p className="text-[#c9a227] uppercase tracking-widest text-sm mb-2">Current Issue</p>
            <h3 className="text-2xl text-[#f5f0e8] mb-1" style={{ fontFamily: "'Cinzel', serif" }}>
              Winter to Spring 2026
            </h3>
            <p className="text-[#f5f0e8] opacity-60">Free Digital Edition</p>
          </div>
          
          <a 
            href="http://www.artsetfree.com/Timescity-WinterToSpring2026-ArtSetFreeCom.pdf"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="timescity-download-btn"
            className="btn-gold inline-flex items-center gap-3 text-lg px-8 py-4"
          >
            <Download size={22} />
            Download Current Issue (PDF)
          </a>
          
          <p className="mt-6 text-sm text-[#f5f0e8] opacity-50">
            Free ebook • PDF format • Read on any device
          </p>
        </div>
      </div>
    </div>
  </section>
);

// Welcome Section
const WelcomeSection = () => (
  <section data-testid="welcome-section" className="py-20 px-6 bg-[#050508]">
    <div className="max-w-4xl mx-auto text-center">
      <h2 className="section-title">Welcome to Plomari</h2>
      <p className="text-xl text-[#f5f0e8] opacity-80 mb-8 max-w-3xl mx-auto leading-relaxed">
        The eternal Kingdom of Plomari, where the past, present and future are in love. Our mission? Simply put: To help set humanity free and remind the world how beautiful life can be.
      </p>
      <blockquote className="text-lg text-[#f5f0e8] italic border-l-4 border-[#c9a227] pl-6 text-left max-w-2xl mx-auto">
        "The Kingdom of Plomari is your world. The Plomarians are your people. You can live for yourself today, or help build tomorrow for everyone."
      </blockquote>
    </div>
  </section>
);

// Books Section
const BooksSection = () => {
  const [enlargedImage, setEnlargedImage] = useState(null);
  const [enlargedTitle, setEnlargedTitle] = useState('');

  const handleImageClick = (imageUrl, title) => {
    setEnlargedImage(imageUrl);
    setEnlargedTitle(title);
  };

  const handleClose = () => {
    setEnlargedImage(null);
    setEnlargedTitle('');
  };

  // Close on Escape key
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        handleClose();
      }
    };
    if (enlargedImage) {
      window.addEventListener('keydown', handleKeyDown);
    }
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [enlargedImage]);

  return (
    <section id="books" data-testid="books-section" className="py-20 px-6">
      <div className="max-w-7xl mx-auto">
        <h2 className="section-title">Download Free Ebooks</h2>
        <p className="section-subtitle">The complete 4,000+ page book series A Love Letter To Humanity - All 22 ebooks available for free download</p>
        <p className="text-center text-[#c9a227]/70 text-sm mb-8">Click any book cover to see it in full size</p>
        
        <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
          {BOOKS.map((book, index) => (
            <div key={index} className="card text-center group hover:border-[#c9a227]/50 transition-all" data-testid={`book-card-${index}`}>
              <div 
                className="relative mb-4 rounded-lg overflow-hidden cursor-pointer" 
                style={{ aspectRatio: "3/4" }}
                onClick={() => handleImageClick(book.image, book.title)}
              >
                <img 
                  src={book.image} 
                  alt={book.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                />
                <div className="absolute inset-0 bg-[#c9a227]/0 group-hover:bg-[#c9a227]/20 transition-colors duration-300 flex items-center justify-center pointer-events-none">
                  <span className="opacity-0 group-hover:opacity-100 transition-opacity text-white text-sm bg-black/70 px-3 py-2 rounded-lg font-medium">
                    Click to enlarge
                  </span>
                </div>
              </div>
              <h3 className="text-base text-[#c9a227] mb-1 line-clamp-2" style={{ fontFamily: "'Cinzel', serif" }}>
                {book.title}
              </h3>
              <p className="text-[#f5f0e8] opacity-70 mb-4 text-sm">{book.pages}</p>
              <div className="flex gap-2 justify-center flex-wrap">
                <a 
                  href={book.downloadUrl} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="btn-gold text-xs px-3 py-2 flex items-center gap-1"
                  data-testid={`download-book-${index}`}
                >
                  <Download size={12} />
                  Download Free
                </a>
                {book.buyUrl && (
                  <a 
                    href={book.buyUrl} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="btn-outline text-xs px-3 py-2 flex items-center gap-1"
                  >
                    <BookOpen size={12} />
                    Hardcover
                  </a>
                )}
              </div>
            </div>
          ))}
        </div>
        
        <div className="text-center mt-12">
          <a 
            href="https://www.lulu.com/search?contributor=King+Spiros+of+Plomari&page=1&pageSize=10&adult_audience_rating=00" 
            target="_blank" 
            rel="noopener noreferrer"
            className="btn-outline inline-flex items-center gap-2"
          >
            <ExternalLink size={18} />
            Buy Hardcover Books on Lulu
          </a>
        </div>
      </div>

      {/* Enlarged Image Modal */}
      {enlargedImage && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/95 backdrop-blur-sm cursor-pointer"
          onClick={handleClose}
        >
          <div className="relative max-w-5xl max-h-[95vh] p-4" onClick={(e) => e.stopPropagation()}>
            {/* Close button */}
            <button 
              onClick={handleClose}
              className="absolute -top-2 -right-2 z-10 bg-[#c9a227] hover:bg-[#f5d77a] text-black rounded-full p-2 transition-colors"
              aria-label="Close"
            >
              <X size={24} />
            </button>
            
            <img 
              src={enlargedImage} 
              alt={enlargedTitle}
              className="max-w-full max-h-[85vh] object-contain rounded-lg shadow-2xl shadow-[#c9a227]/30"
            />
            <h3 
              className="text-center text-[#c9a227] mt-4 text-xl"
              style={{ fontFamily: "'Cinzel', serif" }}
            >
              {enlargedTitle}
            </h3>
            <p className="text-center text-[#f5f0e8]/50 text-sm mt-2">
              Click anywhere outside or press ESC to close
            </p>
          </div>
        </div>
      )}
    </section>
  );
};

// Music Feature Section
const MusicFeatureSection = () => (
  <section data-testid="music-feature-section" className="py-20 px-6 bg-[#050508]">
    <div className="max-w-6xl mx-auto">
      <h2 className="section-title">SISSY COGAN</h2>
      <p className="section-subtitle">The music of the eternal Kingdom - Plomari Radio Free broadcasting since 2005</p>
      
      <div className="grid md:grid-cols-2 gap-8 items-center">
        <div className="rounded-lg overflow-hidden" style={{ aspectRatio: "4/3" }}>
          <img 
            src="https://images.unsplash.com/photo-1767403154501-a677bb969ba6?w=800" 
            alt="Music of Plomari"
            className="w-full h-full object-cover"
          />
        </div>
        <div>
          <h3 className="text-2xl text-[#c9a227] mb-4" style={{ fontFamily: "'Cinzel', serif" }}>
            Experience the Sound of Plomari
          </h3>
          <p className="text-lg text-[#f5f0e8] opacity-80 mb-6 leading-relaxed">
            Electronic, atmospheric, and spiritually awakening music from the eternal Kingdom. 
            Let the sounds of Plomari transport you to realms beyond imagination.
          </p>
          <div className="space-y-4">
            <div className="youtube-container" style={{ paddingBottom: "30%" }}>
              <iframe
                src="https://www.youtube.com/embed/pwX-2FhN_7U?rel=0"
                title="SISSY COGAN - Flee From The One Named Queen Cecilia Cogan"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              ></iframe>
            </div>
            <div className="youtube-container" style={{ paddingBottom: "30%" }}>
              <iframe
                src="https://www.youtube.com/embed/8vePNhiEIfE?rel=0"
                title="SISSY COGAN - Remember What It Means To Be Free"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              ></iframe>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
);

// House of Cards Section
const HouseOfCardsSection = () => (
  <section data-testid="house-of-cards-section" className="py-20 px-6">
    <div className="max-w-4xl mx-auto">
      <h2 className="section-title">The Wind That Topples the House of Cards</h2>
      <p className="text-lg text-[#f5f0e8] opacity-80 mb-8 text-center max-w-3xl mx-auto">
        For millennia, human history has been built like a fragile house of cards—a precarious structure of traditions, systems, and beliefs stacked atop one another, vulnerable to the slightest disruption.
      </p>
      <blockquote className="glass rounded-lg p-8 text-center">
        <p className="text-xl text-[#f5f0e8] italic leading-relaxed mb-4">
          "Human history is like a house of cards, and Plomari is designed to blow it down. This doesn't mean the world will vanish or end; it simply marks the end of bullshit and the beginning of paradise."
        </p>
        <p className="text-[#c9a227]">~ King Spiros of Plomari</p>
      </blockquote>
    </div>
  </section>
);

// FAQ Section
const FAQSection = () => {
  const [openIndex, setOpenIndex] = useState(null);

  return (
    <section id="faq" data-testid="faq-section" className="py-20 px-6 bg-[#050508]">
      <div className="max-w-3xl mx-auto">
        {FAQ_DATA.map((item, index) => (
          <div key={index} className="accordion-item">
            <div 
              className="accordion-header"
              onClick={() => setOpenIndex(openIndex === index ? null : index)}
              data-testid={`faq-item-${index}`}
            >
              <span>{item.question}</span>
              {openIndex === index ? <ChevronUp className="text-[#c9a227]" /> : <ChevronDown className="text-[#c9a227]" />}
            </div>
            {openIndex === index && (
              <div className="accordion-content">
                {item.answer}
              </div>
            )}
          </div>
        ))}
      </div>
    </section>
  );
};

// Support Section
const SupportSection = () => (
  <section data-testid="support-section" className="py-20 px-6">
    <div className="max-w-4xl mx-auto text-center">
      <h2 className="section-title">Support the Kingdom</h2>
      <p className="text-lg text-[#f5f0e8] opacity-80 mb-8 max-w-2xl mx-auto">
        Mostly everything is free in Plomari. But running the eternal Kingdom is expensive and challenging. Your donation helps keep the magic alive and supports the Queen of the Animals in her mission to help wildlife worldwide.
      </p>
      <a 
        href="https://gofund.me/4c6b9be8" 
        target="_blank" 
        rel="noopener noreferrer"
        data-testid="donate-btn"
        className="btn-gold inline-flex items-center gap-2"
      >
        <Heart size={18} />
        Donate to Plomari
      </a>
    </div>
  </section>
);

// Contact Section
const ContactSection = () => (
  <section data-testid="contact-section" className="py-20 px-6 bg-[#050508]">
    <div className="max-w-4xl mx-auto">
      <h2 className="section-title">Connect with King Spiros</h2>
      <p className="section-subtitle">Private phonecalls available for life coaching, collaboration, or conversation about the Kingdom of Plomari. Free for the first few minutes, then $60/hour.</p>
      
      <div className="grid md:grid-cols-3 gap-8">
        <a 
          href="tel:+46725202623" 
          className="card text-center hover:border-[#c9a227] transition-all"
          data-testid="phone-link"
        >
          <Phone size={32} className="text-[#c9a227] mx-auto mb-4" />
          <h3 className="text-lg text-[#c9a227] mb-2" style={{ fontFamily: "'Cinzel', serif" }}>Phone (Sweden)</h3>
          <p className="text-[#f5f0e8]">+46 (0)72 520 26 23</p>
        </a>
        
        <a 
          href="https://www.instagram.com/kingspirosofplomari/" 
          target="_blank" 
          rel="noopener noreferrer"
          className="card text-center hover:border-[#c9a227] transition-all"
          data-testid="instagram-link"
        >
          <Instagram size={32} className="text-[#c9a227] mx-auto mb-4" />
          <h3 className="text-lg text-[#c9a227] mb-2" style={{ fontFamily: "'Cinzel', serif" }}>Instagram</h3>
          <p className="text-[#f5f0e8]">@kingspirosofplomari</p>
        </a>
        
        <a 
          href="https://www.youtube.com/channel/UCMw4s7JLSDhNlq4Ul93ljug" 
          target="_blank" 
          rel="noopener noreferrer"
          className="card text-center hover:border-[#c9a227] transition-all"
          data-testid="youtube-link"
        >
          <Youtube size={32} className="text-[#c9a227] mx-auto mb-4" />
          <h3 className="text-lg text-[#c9a227] mb-2" style={{ fontFamily: "'Cinzel', serif" }}>YouTube</h3>
          <p className="text-[#f5f0e8]">King Spiros of Plomari</p>
        </a>
      </div>
    </div>
  </section>
);

// Footer
const Footer = () => (
  <footer className="py-8 px-6 border-t border-[rgba(201,162,39,0.2)]">
    <div className="max-w-4xl mx-auto text-center">
      <a 
        href="https://app.emergent.sh/?utm_source=emergent-badge" 
        target="_blank" 
        rel="noopener noreferrer"
        className="text-[#f5f0e8] opacity-50 hover:opacity-80 transition-opacity text-sm"
      >
        Made with Emergent
      </a>
    </div>
  </footer>
);

// Main App Component
function App() {
  return (
    <div className="App">
      <div className="noise-overlay"></div>
      <HeroSection />
      <QuoteSection />
      <YouTubeSection />
      <VisualJourneySection />
      <GuestBookSection />
      <TimescitySection />
      <WelcomeSection />
      <BooksSection />
      <MusicFeatureSection />
      <HouseOfCardsSection />
      <FAQSection />
      <SupportSection />
      <ContactSection />
      <Footer />
    </div>
  );
}

// Admin Panel Component - Sleek & Technological
const AdminPanel = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState("");
  const [storedPassword, setStoredPassword] = useState("");
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [filter, setFilter] = useState("all"); // all, pending, approved, rejected

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const response = await axios.post(`${API}/admin/login`, { password });
      if (response.data.success) {
        setIsAuthenticated(true);
        setStoredPassword(password);
        fetchEntries(password);
      }
    } catch (err) {
      setError("Invalid password. Access denied.");
    }
    setLoading(false);
  };

  const fetchEntries = async (pwd) => {
    try {
      const response = await axios.get(`${API}/admin/guestbook?password=${pwd || storedPassword}`);
      setEntries(response.data);
    } catch (err) {
      console.error("Error fetching entries:", err);
    }
  };

  const handleAction = async (entryId, action) => {
    try {
      await axios.post(`${API}/admin/guestbook/action?password=${storedPassword}`, {
        entry_id: entryId,
        action: action
      });
      fetchEntries();
    } catch (err) {
      console.error("Error performing action:", err);
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    setStoredPassword("");
    setPassword("");
    setEntries([]);
  };

  const filteredEntries = entries.filter(entry => {
    if (filter === "all") return true;
    return entry.status === filter;
  });

  const pendingCount = entries.filter(e => e.status === "pending").length;
  const approvedCount = entries.filter(e => e.status === "approved").length;
  const rejectedCount = entries.filter(e => e.status === "rejected").length;

  const formatDate = (timestamp) => {
    return new Date(timestamp).toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  // Login Screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-[#050508] flex items-center justify-center px-6">
        <div className="w-full max-w-md">
          {/* Tech grid background */}
          <div className="absolute inset-0 opacity-5">
            <div className="absolute inset-0" style={{
              backgroundImage: `linear-gradient(rgba(201,162,39,0.3) 1px, transparent 1px),
                               linear-gradient(90deg, rgba(201,162,39,0.3) 1px, transparent 1px)`,
              backgroundSize: '50px 50px'
            }}></div>
          </div>
          
          <div className="relative glass rounded-2xl p-8 border border-[#c9a227]/30">
            <div className="text-center mb-8">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#c9a227]/10 border border-[#c9a227]/30 mb-4">
                <Shield size={32} className="text-[#c9a227]" />
              </div>
              <h1 className="text-2xl text-[#c9a227] mb-2" style={{ fontFamily: "'Cinzel', serif" }}>
                Admin Access
              </h1>
              <p className="text-[#f5f0e8]/60 text-sm">Kingdom of Plomari Control Center</p>
            </div>
            
            <form onSubmit={handleLogin}>
              <div className="relative mb-6">
                <Lock size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#c9a227]/50" />
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter royal password"
                  className="w-full pl-12 pr-4 py-4 bg-[#0a0a0f] border border-[#c9a227]/30 rounded-lg text-[#f5f0e8] focus:border-[#c9a227] focus:outline-none transition-all"
                  data-testid="admin-password-input"
                />
              </div>
              
              {error && (
                <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm text-center">
                  {error}
                </div>
              )}
              
              <button
                type="submit"
                disabled={loading}
                className="w-full btn-gold py-4 flex items-center justify-center gap-2"
                data-testid="admin-login-btn"
              >
                {loading ? "Authenticating..." : "Access Control Center"}
              </button>
            </form>
            
            <div className="mt-6 pt-6 border-t border-[#c9a227]/20 text-center">
              <a href="/" className="text-[#f5f0e8]/50 hover:text-[#c9a227] text-sm transition-colors">
                ← Return to Kingdom
              </a>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Admin Dashboard
  return (
    <div className="min-h-screen bg-[#050508]">
      {/* Tech grid background */}
      <div className="fixed inset-0 opacity-5 pointer-events-none">
        <div className="absolute inset-0" style={{
          backgroundImage: `linear-gradient(rgba(201,162,39,0.3) 1px, transparent 1px),
                           linear-gradient(90deg, rgba(201,162,39,0.3) 1px, transparent 1px)`,
          backgroundSize: '50px 50px'
        }}></div>
      </div>
      
      {/* Header */}
      <header className="sticky top-0 z-50 glass border-b border-[#c9a227]/20">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Shield size={24} className="text-[#c9a227]" />
            <div>
              <h1 className="text-xl text-[#c9a227]" style={{ fontFamily: "'Cinzel', serif" }}>
                Guest Book Admin
              </h1>
              <p className="text-xs text-[#f5f0e8]/50">Kingdom Control Center</p>
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="flex items-center gap-2 text-[#f5f0e8]/70 hover:text-[#c9a227] transition-colors"
            data-testid="admin-logout-btn"
          >
            <LogOut size={18} />
            <span className="hidden sm:inline">Logout</span>
          </button>
        </div>
      </header>
      
      <main className="relative max-w-6xl mx-auto px-6 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="glass rounded-xl p-4 border border-[#c9a227]/20">
            <p className="text-[#f5f0e8]/50 text-xs uppercase tracking-wider mb-1">Total</p>
            <p className="text-2xl text-[#f5f0e8]">{entries.length}</p>
          </div>
          <div 
            className={`glass rounded-xl p-4 border cursor-pointer transition-all ${filter === 'pending' ? 'border-yellow-500' : 'border-yellow-500/20 hover:border-yellow-500/50'}`}
            onClick={() => setFilter(filter === 'pending' ? 'all' : 'pending')}
          >
            <p className="text-yellow-500/70 text-xs uppercase tracking-wider mb-1 flex items-center gap-1">
              <Clock size={12} /> Pending
            </p>
            <p className="text-2xl text-yellow-500">{pendingCount}</p>
          </div>
          <div 
            className={`glass rounded-xl p-4 border cursor-pointer transition-all ${filter === 'approved' ? 'border-green-500' : 'border-green-500/20 hover:border-green-500/50'}`}
            onClick={() => setFilter(filter === 'approved' ? 'all' : 'approved')}
          >
            <p className="text-green-500/70 text-xs uppercase tracking-wider mb-1 flex items-center gap-1">
              <CheckCircle size={12} /> Approved
            </p>
            <p className="text-2xl text-green-500">{approvedCount}</p>
          </div>
          <div 
            className={`glass rounded-xl p-4 border cursor-pointer transition-all ${filter === 'rejected' ? 'border-red-500' : 'border-red-500/20 hover:border-red-500/50'}`}
            onClick={() => setFilter(filter === 'rejected' ? 'all' : 'rejected')}
          >
            <p className="text-red-500/70 text-xs uppercase tracking-wider mb-1 flex items-center gap-1">
              <XCircle size={12} /> Rejected
            </p>
            <p className="text-2xl text-red-500">{rejectedCount}</p>
          </div>
        </div>
        
        {/* Filter indicator */}
        {filter !== 'all' && (
          <div className="mb-4 flex items-center gap-2">
            <span className="text-[#f5f0e8]/50 text-sm">Filtering by: </span>
            <span className={`px-2 py-1 rounded text-xs uppercase ${
              filter === 'pending' ? 'bg-yellow-500/20 text-yellow-500' :
              filter === 'approved' ? 'bg-green-500/20 text-green-500' :
              'bg-red-500/20 text-red-500'
            }`}>{filter}</span>
            <button onClick={() => setFilter('all')} className="text-[#f5f0e8]/50 hover:text-[#c9a227] text-sm">
              (clear)
            </button>
          </div>
        )}
        
        {/* Entries List */}
        <div className="space-y-4">
          {filteredEntries.length === 0 ? (
            <div className="glass rounded-xl p-12 text-center border border-[#c9a227]/20">
              <p className="text-[#f5f0e8]/50">No messages found</p>
            </div>
          ) : (
            filteredEntries.map((entry) => (
              <div 
                key={entry.id} 
                className={`glass rounded-xl p-6 border transition-all ${
                  entry.status === 'pending' ? 'border-yellow-500/30' :
                  entry.status === 'approved' ? 'border-green-500/30' :
                  'border-red-500/30'
                }`}
                data-testid={`admin-entry-${entry.id}`}
              >
                <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <span className={`px-2 py-0.5 rounded text-xs uppercase ${
                        entry.status === 'pending' ? 'bg-yellow-500/20 text-yellow-500' :
                        entry.status === 'approved' ? 'bg-green-500/20 text-green-500' :
                        'bg-red-500/20 text-red-500'
                      }`}>
                        {entry.status}
                      </span>
                      <span className="text-[#f5f0e8]/40 text-xs">{formatDate(entry.timestamp)}</span>
                    </div>
                    <p className="text-[#c9a227] font-semibold">{entry.name}</p>
                    {entry.location && <p className="text-[#f5f0e8]/50 text-sm">{entry.location}</p>}
                    <p className="text-[#f5f0e8] mt-2 leading-relaxed">"{entry.message}"</p>
                  </div>
                  
                  <div className="flex gap-2 md:flex-col">
                    {entry.status === 'pending' && (
                      <>
                        <button
                          onClick={() => handleAction(entry.id, 'approve')}
                          className="flex items-center gap-1 px-3 py-2 bg-green-500/20 text-green-500 rounded-lg hover:bg-green-500/30 transition-colors text-sm"
                          data-testid={`approve-btn-${entry.id}`}
                        >
                          <Check size={16} /> Approve
                        </button>
                        <button
                          onClick={() => handleAction(entry.id, 'reject')}
                          className="flex items-center gap-1 px-3 py-2 bg-red-500/20 text-red-500 rounded-lg hover:bg-red-500/30 transition-colors text-sm"
                          data-testid={`reject-btn-${entry.id}`}
                        >
                          <X size={16} /> Reject
                        </button>
                      </>
                    )}
                    {entry.status === 'rejected' && (
                      <button
                        onClick={() => handleAction(entry.id, 'approve')}
                        className="flex items-center gap-1 px-3 py-2 bg-green-500/20 text-green-500 rounded-lg hover:bg-green-500/30 transition-colors text-sm"
                      >
                        <Check size={16} /> Approve
                      </button>
                    )}
                    {entry.status === 'approved' && (
                      <button
                        onClick={() => handleAction(entry.id, 'reject')}
                        className="flex items-center gap-1 px-3 py-2 bg-yellow-500/20 text-yellow-500 rounded-lg hover:bg-yellow-500/30 transition-colors text-sm"
                      >
                        <X size={16} /> Unapprove
                      </button>
                    )}
                    <button
                      onClick={() => handleAction(entry.id, 'delete')}
                      className="flex items-center gap-1 px-3 py-2 bg-[#f5f0e8]/10 text-[#f5f0e8]/50 rounded-lg hover:bg-red-500/20 hover:text-red-500 transition-colors text-sm"
                      data-testid={`delete-btn-${entry.id}`}
                    >
                      <Trash2 size={16} /> Delete
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
        
        {/* Footer */}
        <div className="mt-12 text-center">
          <a href="/" className="text-[#f5f0e8]/50 hover:text-[#c9a227] text-sm transition-colors">
            ← Return to Kingdom
          </a>
        </div>
      </main>
    </div>
  );
};

// Home Page Component
const HomePage = () => (
  <>
    <Navigation />
    <div className="noise-overlay"></div>
    <HeroSection />
    <RoyalMessageSection />
    <QuoteSection />
    <YouTubeSection />
    <VisualJourneySection />
    <GuestBookSection />
    <TimescitySection />
    <WelcomeSection />
    <BooksSection />
    <MusicFeatureSection />
    <HouseOfCardsSection />
    <FAQSection />
    <SupportSection />
    <ContactSection />
    <Footer />
  </>
);

// Royal Message Admin Panel
const RoyalMessageAdmin = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState("");
  const [storedPassword, setStoredPassword] = useState("");
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const response = await axios.post(`${API}/admin/login`, { password });
      if (response.data.success) {
        setIsAuthenticated(true);
        setStoredPassword(password);
        fetchMessages(password);
      }
    } catch (err) {
      setError("Invalid password. Access denied.");
    }
    setLoading(false);
  };

  const fetchMessages = async (pwd) => {
    try {
      const response = await axios.get(`${API}/royal-messages?password=${pwd || storedPassword}`);
      setMessages(response.data);
    } catch (err) {
      console.error("Error fetching messages:", err);
    }
  };

  const handlePost = async (e) => {
    e.preventDefault();
    if (!newMessage.trim()) return;
    
    setLoading(true);
    setError("");
    setSuccess("");
    try {
      await axios.post(`${API}/royal-message`, {
        message: newMessage,
        password: storedPassword
      });
      setSuccess("Royal message posted successfully!");
      setNewMessage("");
      fetchMessages();
    } catch (err) {
      setError("Failed to post message");
    }
    setLoading(false);
  };

  const handleDelete = async (messageId) => {
    try {
      await axios.delete(`${API}/royal-message/${messageId}?password=${storedPassword}`);
      fetchMessages();
    } catch (err) {
      console.error("Error deleting message:", err);
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    setStoredPassword("");
    setPassword("");
    setMessages([]);
  };

  const formatDate = (timestamp) => {
    return new Date(timestamp).toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  // Login Screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-[#050508] flex items-center justify-center px-6">
        <div className="w-full max-w-md">
          <div className="absolute inset-0 opacity-5">
            <div className="absolute inset-0" style={{
              backgroundImage: `linear-gradient(rgba(201,162,39,0.3) 1px, transparent 1px),
                               linear-gradient(90deg, rgba(201,162,39,0.3) 1px, transparent 1px)`,
              backgroundSize: '50px 50px'
            }}></div>
          </div>
          
          <div className="relative glass rounded-2xl p-8 border border-[#c9a227]/30">
            <div className="text-center mb-8">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#c9a227]/10 border border-[#c9a227]/30 mb-4">
                <Crown size={32} className="text-[#c9a227]" />
              </div>
              <h1 className="text-2xl text-[#c9a227] mb-2" style={{ fontFamily: "'Cinzel', serif" }}>
                Royal Broadcast
              </h1>
              <p className="text-[#f5f0e8]/60 text-sm">Post messages to your Kingdom</p>
            </div>
            
            <form onSubmit={handleLogin}>
              <div className="relative mb-6">
                <Lock size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#c9a227]/50" />
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter royal password"
                  className="w-full pl-12 pr-4 py-4 bg-[#0a0a0f] border border-[#c9a227]/30 rounded-lg text-[#f5f0e8] focus:border-[#c9a227] focus:outline-none transition-all"
                  data-testid="royal-password-input"
                />
              </div>
              
              {error && (
                <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm text-center">
                  {error}
                </div>
              )}
              
              <button
                type="submit"
                disabled={loading}
                className="w-full btn-gold py-4"
                data-testid="royal-login-btn"
              >
                {loading ? "Authenticating..." : "Access Royal Broadcast"}
              </button>
            </form>
            
            <div className="mt-6 pt-6 border-t border-[#c9a227]/20 text-center">
              <a href="/" className="text-[#f5f0e8]/50 hover:text-[#c9a227] text-sm transition-colors">
                ← Return to Kingdom
              </a>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Admin Dashboard
  return (
    <div className="min-h-screen bg-[#050508]">
      <div className="fixed inset-0 opacity-5 pointer-events-none">
        <div className="absolute inset-0" style={{
          backgroundImage: `linear-gradient(rgba(201,162,39,0.3) 1px, transparent 1px),
                           linear-gradient(90deg, rgba(201,162,39,0.3) 1px, transparent 1px)`,
          backgroundSize: '50px 50px'
        }}></div>
      </div>
      
      <header className="sticky top-0 z-50 glass border-b border-[#c9a227]/20">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Crown size={24} className="text-[#c9a227]" />
            <div>
              <h1 className="text-xl text-[#c9a227]" style={{ fontFamily: "'Cinzel', serif" }}>
                Royal Broadcast
              </h1>
              <p className="text-xs text-[#f5f0e8]/50">Message your Kingdom</p>
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="flex items-center gap-2 text-[#f5f0e8]/70 hover:text-[#c9a227] transition-colors"
          >
            <LogOut size={18} />
            <span className="hidden sm:inline">Logout</span>
          </button>
        </div>
      </header>
      
      <main className="relative max-w-4xl mx-auto px-6 py-8">
        {/* Post New Message */}
        <div className="glass rounded-xl p-6 border border-[#c9a227]/30 mb-8">
          <h2 className="text-lg text-[#c9a227] mb-4" style={{ fontFamily: "'Cinzel', serif" }}>
            Post New Royal Message
          </h2>
          <form onSubmit={handlePost}>
            <textarea
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              placeholder="Write your message to the Kingdom..."
              rows={4}
              className="w-full mb-4 bg-[#0a0a0f] border border-[#c9a227]/30 rounded-lg text-[#f5f0e8] p-4 focus:border-[#c9a227] focus:outline-none"
              data-testid="royal-message-textarea"
            />
            
            {error && (
              <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm">
                {error}
              </div>
            )}
            
            {success && (
              <div className="mb-4 p-3 bg-green-500/10 border border-green-500/30 rounded-lg text-green-400 text-sm">
                {success}
              </div>
            )}
            
            <button
              type="submit"
              disabled={loading || !newMessage.trim()}
              className="btn-gold"
              data-testid="post-royal-message-btn"
            >
              {loading ? "Posting..." : "Post to Kingdom"}
            </button>
          </form>
        </div>
        
        {/* Past Messages */}
        <h2 className="text-lg text-[#c9a227] mb-4" style={{ fontFamily: "'Cinzel', serif" }}>
          Past Royal Messages
        </h2>
        <div className="space-y-4">
          {messages.length === 0 ? (
            <div className="glass rounded-xl p-8 text-center border border-[#c9a227]/20">
              <p className="text-[#f5f0e8]/50">No messages yet. Post your first royal message!</p>
            </div>
          ) : (
            messages.map((msg) => (
              <div key={msg.id} className="glass rounded-xl p-6 border border-[#c9a227]/20">
                <p className="text-[#f5f0e8] mb-3 whitespace-pre-wrap">{msg.message}</p>
                <div className="flex items-center justify-between">
                  <p className="text-sm text-[#c9a227]/60">{formatDate(msg.timestamp)}</p>
                  <button
                    onClick={() => handleDelete(msg.id)}
                    className="flex items-center gap-1 px-3 py-1 text-red-400 hover:bg-red-500/20 rounded transition-colors text-sm"
                  >
                    <Trash2 size={14} /> Delete
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
        
        <div className="mt-12 text-center">
          <a href="/" className="text-[#f5f0e8]/50 hover:text-[#c9a227] text-sm transition-colors">
            ← Return to Kingdom
          </a>
        </div>
      </main>
    </div>
  );
};

// Main App with Routing
function AppWithRoutes() {
  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/admin" element={<AdminPanel />} />
          <Route path="/royal" element={<RoyalMessageAdmin />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default AppWithRoutes;
